import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Pencil } from "lucide-react"
import {
  api,
  bricKvpProjektAb,
  bricKvpTeilprojektAb,
  createKvpAufgabe,
  createKvpProjekt,
  createKvpTeilprojekt,
  fetchKvpAufgaben,
  fetchKvpProjekte,
  fetchKvpStatus,
  fetchKvpTeilprojekte,
  fetchMeinCockpit,
  hebeKvpProjektAbbruchAuf,
  hebeKvpTeilprojektAbbruchAuf,
  updateKvpProjekt,
  updateKvpTeilprojekt,
  type KvpAbbruchFelder,
  type KvpAbgeleiteterStatus,
  type KvpAufgabe,
  type KvpAufgabenZaehler,
  type KvpProjekt,
  type KvpStatus,
  type KvpTeilprojekt,
} from "@/lib/api"
import { Badge, Button, Input, Select, Textarea } from "@/components/ui"
import { DataGrid, type ColumnDef } from "@/components/DataGrid"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SHEET_WIDTH,
} from "@/components/Sheet"
import { EmployeePicker, type EmployeeOption } from "@/components/EmployeePicker"
import { useToast, fehlerNachricht } from "@/lib/toast"
import { KvpAufgabeDetailContent } from "./KvpAufgabeDetailPage"

/** Anzeigenamen der Projekt-Arten (05-A, CHECK-Constraint im Backend). */
const PROJEKT_ARTEN: Array<{ wert: string; label: string }> = [
  { wert: "projekt", label: "Projekt" },
  { wert: "anlagenbereich", label: "Anlagenbereich" },
  { wert: "sachthema", label: "Sachthema" },
]

const PRIORITAETEN = ["hoch", "mittel", "gering"] as const

function artLabel(art: string): string {
  return PROJEKT_ARTEN.find((a) => a.wert === art)?.label ?? art
}

function formatDatum(wert: string | null | undefined): string {
  if (!wert) return "—"
  const d = new Date(wert)
  return Number.isNaN(d.getTime()) ? wert : d.toLocaleDateString("de-DE")
}

/** ISO-/Datetime-String auf den Wert eines <input type="date"> kürzen. */
function alsDateInput(wert: string | null | undefined): string {
  return wert ? wert.slice(0, 10) : ""
}

// ---------------------------------------------------------------------------
// Abgeleiteter Ebenen-Status (Backend leite_status_ab): Anzeige-Helfer
// ---------------------------------------------------------------------------

const ABGELEITET_LABEL: Record<KvpAbgeleiteterStatus, string> = {
  neu: "neu",
  in_arbeit: "in Arbeit",
  abgeschlossen: "abgeschlossen",
  verworfen: "verworfen",
  abgebrochen: "abgebrochen",
}

const ABGELEITET_KLASSE: Record<KvpAbgeleiteterStatus, string> = {
  neu: "bg-slate-100 text-slate-600",
  in_arbeit: "bg-sky-100 text-sky-800",
  abgeschlossen: "bg-green-100 text-green-800",
  verworfen: "bg-red-100 text-red-700",
  abgebrochen: "bg-red-900 text-red-50",
}

/** Fortschrittstext „4/5 erledigt · 1 verworfen" aus den Aufgaben-Zählern. */
function fortschrittText(z: KvpAufgabenZaehler): string | null {
  if (z.aufgabenGesamt === 0) return null
  const teile = [`${z.aufgabenErledigt}/${z.aufgabenGesamt} erledigt`]
  if (z.aufgabenVerworfen > 0) teile.push(`${z.aufgabenVerworfen} verworfen`)
  return teile.join(" · ")
}

function AbgeleiteterStatusBadge({
  status,
  abbruch,
}: {
  status: KvpAbgeleiteterStatus
  abbruch?: KvpAbbruchFelder
}) {
  // Beim Abbruch trägt der Tooltip Grund/Datum/von + konservierten Vorher-Stand
  const tooltip =
    status === "abgebrochen" && abbruch?.abgebrochenAm
      ? `Grund: ${abbruch.abbruchGrund ?? "—"}\nAm: ${formatDatum(abbruch.abgebrochenAm)}${
          abbruch.abgebrochenVon ? `\nVon: ${abbruch.abgebrochenVon}` : ""
        }${
          abbruch.statusBeiAbbruch
            ? `\nWar: ${ABGELEITET_LABEL[abbruch.statusBeiAbbruch as KvpAbgeleiteterStatus] ?? abbruch.statusBeiAbbruch}`
            : ""
        }`
      : undefined
  return (
    <span
      title={tooltip}
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${ABGELEITET_KLASSE[status]}`}
    >
      {ABGELEITET_LABEL[status]}
    </span>
  )
}

/**
 * KVP-Board (AP-4.8): Projekte → Teilprojekte → Aufgaben + persönliches
 * Cockpit (05-G „Aufgabentacho").
 *
 * UX-Umbau (UX_KONZEPT.md §7): Anlegen/Bearbeiten läuft über prominente
 * „+ Neu…"-Buttons und Sheets von rechts (Muster PruefungenPage), die
 * Aufgabenliste ist ein DataGrid mit Status-/Prioritäts-/Fortschritts-Spalten,
 * über dem Grid zeigt eine Kontextzeile (Breadcrumb) die aktive Auswahl.
 */
export function KvpBoardPage() {
  const queryClient = useQueryClient()
  const [ausgewaehltesProjekt, setAusgewaehltesProjekt] = useState<number | null>(null)
  const [ausgewaehltesTeilprojekt, setAusgewaehltesTeilprojekt] = useState<number | null>(null)
  const [nurOffene, setNurOffene] = useState(false)
  const [nurMeine, setNurMeine] = useState(false)
  const [detailAufgabeId, setDetailAufgabeId] = useState<number | null>(null)

  // Sheet-Zustände: "neu" = Anlegen, Objekt = Bearbeiten, null = geschlossen
  const [projektSheet, setProjektSheet] = useState<"neu" | KvpProjekt | null>(null)
  const [teilprojektSheet, setTeilprojektSheet] = useState<"neu" | KvpTeilprojekt | null>(null)
  const [aufgabeSheetOffen, setAufgabeSheetOffen] = useState(false)

  const { data: cockpit } = useQuery({ queryKey: ["kvp-cockpit"], queryFn: fetchMeinCockpit })
  const { data: projekte } = useQuery({ queryKey: ["kvp-projekte"], queryFn: fetchKvpProjekte })
  const { data: teilprojekte } = useQuery({
    queryKey: ["kvp-teilprojekte", ausgewaehltesProjekt],
    queryFn: () => fetchKvpTeilprojekte(ausgewaehltesProjekt ?? undefined),
    enabled: ausgewaehltesProjekt !== null,
  })
  const { data: statusListe } = useQuery({
    queryKey: ["kvp-status"],
    queryFn: () => fetchKvpStatus(),
  })
  const { data: employees } = useQuery({
    queryKey: ["employees", "all"],
    queryFn: async () =>
      (await api.get<EmployeeOption[]>("/employees", { params: { nur_aktive: false } })).data,
    staleTime: 5 * 60_000,
  })
  const { data: aufgaben, isLoading: aufgabenLaden } = useQuery({
    queryKey: [
      "kvp-aufgaben",
      { teilprojektId: ausgewaehltesTeilprojekt, nurOffene, nurMeine },
    ],
    queryFn: () =>
      fetchKvpAufgaben({
        teilprojektId: ausgewaehltesTeilprojekt ?? undefined,
        nurOffene: nurOffene || undefined,
        rolle: nurMeine ? "zustaendig" : undefined,
      }),
  })

  const projekt = projekte?.find((p) => p.id === ausgewaehltesProjekt) ?? null
  const teilprojekt = teilprojekte?.find((tb) => tb.id === ausgewaehltesTeilprojekt) ?? null

  const statusName = (id: number | null) =>
    id === null ? null : statusListe?.find((s) => s.id === id) ?? null
  const mitarbeiterName = (id: string | null) => {
    if (!id) return "—"
    const e = employees?.find((e) => e.id === id)
    return e ? (e.kuerzel ? `${e.name} (${e.kuerzel})` : e.name) : id
  }

  // Grid-Spalten (header als String → Spalten-Auswahl kann ihn anzeigen)
  const spalten = useMemo<ColumnDef<KvpAufgabe>[]>(
    () => [
      {
        id: "businessId",
        header: "Business-ID",
        accessorFn: (a) => a.businessId ?? "",
        cell: ({ row }) => (
          <span className="whitespace-nowrap font-mono text-xs text-slate-500">
            {row.original.businessId ?? "—"}
          </span>
        ),
      },
      {
        id: "bezeichnung",
        header: "Bezeichnung",
        accessorFn: (a) => a.bezeichnung,
        cell: ({ row }) => (
          <span className="line-clamp-2 max-w-xl font-medium text-slate-900">
            {row.original.bezeichnung}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (a) => statusName(a.statusId)?.name ?? "",
        cell: ({ row }) => {
          const s = statusName(row.original.statusId)
          if (!s) return <span className="text-slate-400">—</span>
          return (
            <span
              className="inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium text-slate-800"
              style={{ backgroundColor: s.farbe ?? "#e2e8f0" }}
            >
              {s.name}
            </span>
          )
        },
      },
      {
        id: "prioritaet",
        header: "Priorität",
        accessorFn: (a) => a.prioritaet ?? "",
        cell: ({ row }) => {
          const p = row.original.prioritaet
          if (!p) return <span className="text-slate-400">—</span>
          return <Badge tone={p === "hoch" ? "red" : p === "mittel" ? "orange" : "slate"}>{p}</Badge>
        },
      },
      {
        id: "fortschritt",
        header: "Fortschritt",
        accessorFn: (a) => a.fortschrittProzent ?? -1,
        cell: ({ row }) => {
          const p = row.original.fortschrittProzent
          if (p == null) return <span className="text-slate-400">—</span>
          return (
            <span className="flex items-center gap-2 whitespace-nowrap">
              <span className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100">
                <span
                  className="block h-full rounded-full bg-slate-500"
                  style={{ width: `${Math.min(Math.max(p, 0), 100)}%` }}
                />
              </span>
              <span className="text-xs tabular-nums text-slate-600">{p} %</span>
            </span>
          )
        },
      },
      {
        id: "geplantesEnde",
        header: "Geplantes Ende",
        accessorFn: (a) => a.geplantesEnde ?? "",
        cell: ({ row }) => (
          <span className="whitespace-nowrap">{formatDatum(row.original.geplantesEnde)}</span>
        ),
      },
      {
        id: "zustaendig",
        header: "Zuständig",
        accessorFn: (a) => mitarbeiterName(a.zustaendigEmployeeId),
        cell: ({ row }) => (
          <span className="whitespace-nowrap">
            {mitarbeiterName(row.original.zustaendigEmployeeId)}
          </span>
        ),
      },
    ],
    // statusName/mitarbeiterName hängen an den Query-Daten
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusListe, employees],
  )

  const invalidateAufgaben = () => {
    queryClient.invalidateQueries({ queryKey: ["kvp-aufgaben"] })
    queryClient.invalidateQueries({ queryKey: ["kvp-aufgaben-alle"] })
    queryClient.invalidateQueries({ queryKey: ["kvp-cockpit"] })
    // Aufgaben-Status treibt die abgeleiteten Ebenen-Status
    queryClient.invalidateQueries({ queryKey: ["kvp-teilprojekte"] })
    queryClient.invalidateQueries({ queryKey: ["kvp-projekte"] })
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">KVP</h1>
          <p className="mt-1 text-sm text-slate-600">
            Projekte → Teilprojekte → Aufgaben (Ninox 05) — hierarchische Business-IDs.
          </p>
        </div>
        {/* Cockpit (05-G „Aufgabentacho"): eigene vs. gesamte offene Aufgaben */}
        {cockpit && (
          <div className="flex items-center gap-6 rounded-lg border border-slate-200 bg-white px-5 py-3 shadow-sm">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                Meine offenen Aufgaben
              </p>
              <p className="text-2xl font-semibold tabular-nums text-slate-900">
                {cockpit.offeneAufgabenEigene}
              </p>
            </div>
            <div className="h-10 w-px bg-slate-200" aria-hidden="true" />
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                Offen gesamt
              </p>
              <p className="text-2xl font-semibold tabular-nums text-slate-900">
                {cockpit.offeneAufgabenGesamt}
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              className="h-8 px-3 text-xs"
              onClick={() => {
                setNurMeine(true)
                setNurOffene(true)
              }}
            >
              Meine Aufgaben anzeigen
            </Button>
          </div>
        )}
      </header>

      {/* Ebene 1 + 2: Projekt- und Teilprojekt-Auswahl */}
      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ProjektPanel
          projekte={projekte}
          ausgewaehlt={ausgewaehltesProjekt}
          onAuswahl={(id) => {
            setAusgewaehltesProjekt(id)
            setAusgewaehltesTeilprojekt(null)
          }}
          onNeu={() => setProjektSheet("neu")}
          onBearbeiten={(p) => setProjektSheet(p)}
        />
        <TeilprojektPanel
          projekt={projekt}
          teilprojekte={ausgewaehltesProjekt !== null ? teilprojekte : undefined}
          ausgewaehlt={ausgewaehltesTeilprojekt}
          onAuswahl={setAusgewaehltesTeilprojekt}
          onNeu={() => setTeilprojektSheet("neu")}
          onBearbeiten={(tb) => setTeilprojektSheet(tb)}
        />
      </div>

      {/* Ebene 3: Aufgaben als DataGrid mit Kontextzeile (Breadcrumb) */}
      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-slate-700">
              Aufgaben{aufgaben ? ` (${aufgaben.length})` : ""}
            </h2>
            {teilprojekt && projekt ? (
              <p className="mt-0.5 truncate text-sm text-slate-500">
                <span className="font-mono text-xs text-slate-400">{projekt.businessId}</span>{" "}
                {projekt.beschreibung}{" "}
                <span aria-hidden="true" className="text-slate-300">
                  ›
                </span>{" "}
                <span className="font-mono text-xs text-slate-400">{teilprojekt.businessId}</span>{" "}
                {teilprojekt.bezeichnung}{" "}
                <AbgeleiteterStatusBadge
                  status={teilprojekt.abgeleiteterStatus}
                  abbruch={teilprojekt}
                />
              </p>
            ) : (
              <p className="mt-0.5 text-sm text-slate-500">
                Alle Aufgaben — für den Kontext oben ein Projekt und Teilprojekt auswählen.
              </p>
            )}
          </div>
          <Button
            type="button"
            onClick={() => setAufgabeSheetOffen(true)}
            disabled={ausgewaehltesTeilprojekt === null}
            title={
              ausgewaehltesTeilprojekt === null
                ? "Zuerst ein Teilprojekt auswählen"
                : undefined
            }
          >
            + Neue Aufgabe
          </Button>
        </div>
        {ausgewaehltesTeilprojekt === null && (
          <p className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
            Neue Aufgaben werden immer einem Teilprojekt zugeordnet — wählen Sie oben ein Projekt
            und Teilprojekt, um „+ Neue Aufgabe“ zu aktivieren.
          </p>
        )}

        {aufgabenLaden && (
          <div className="animate-pulse space-y-2" aria-label="Lade Aufgaben …">
            <div className="h-8 rounded-md bg-slate-100" />
            <div className="h-8 rounded-md bg-slate-100" />
            <div className="h-8 rounded-md bg-slate-100" />
          </div>
        )}
        {aufgaben && (
          <DataGrid
            columns={spalten}
            data={aufgaben}
            storageKey="kvp-aufgaben"
            onRowClick={(a) => setDetailAufgabeId(a.id)}
            emptyMessage={
              ausgewaehltesTeilprojekt !== null
                ? "Noch keine Aufgaben in diesem Teilprojekt — legen Sie die erste über „+ Neue Aufgabe“ an."
                : "Keine Aufgaben gefunden."
            }
            toolbar={
              <>
                <label className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={nurOffene}
                    onChange={(e) => setNurOffene(e.target.checked)}
                    className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                  />
                  Nur offene
                </label>
                <label className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={nurMeine}
                    onChange={(e) => setNurMeine(e.target.checked)}
                    className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                  />
                  Nur meine
                </label>
              </>
            }
          />
        )}
      </section>

      {/* Sheets: Anlegen/Bearbeiten (UX_KONZEPT.md §7, Muster PruefungSheet) */}
      <ProjektSheet
        zustand={projektSheet}
        onOpenChange={(open) => !open && setProjektSheet(null)}
        onGespeichert={(p) => {
          queryClient.invalidateQueries({ queryKey: ["kvp-projekte"] })
          if (projektSheet === "neu") {
            setAusgewaehltesProjekt(p.id)
            setAusgewaehltesTeilprojekt(null)
          }
        }}
      />
      {projekt && (
        <TeilprojektSheet
          zustand={teilprojektSheet}
          projekt={projekt}
          onOpenChange={(open) => !open && setTeilprojektSheet(null)}
          onGespeichert={(tb) => {
            queryClient.invalidateQueries({ queryKey: ["kvp-teilprojekte"] })
            if (teilprojektSheet === "neu") setAusgewaehltesTeilprojekt(tb.id)
          }}
        />
      )}
      {teilprojekt && projekt && (
        <AufgabeNeuSheet
          open={aufgabeSheetOffen}
          projekt={projekt}
          teilprojekt={teilprojekt}
          statusListe={statusListe}
          onOpenChange={setAufgabeSheetOffen}
          onGespeichert={invalidateAufgaben}
        />
      )}

      {/* Detail-Sheet von rechts (UX_KONZEPT.md §7) — Zeilen-Klick öffnet statt zu
          navigieren; `/kvp/aufgaben/:id` bleibt als Deep-Link-Fallback. */}
      <Sheet
        open={detailAufgabeId !== null}
        onOpenChange={(open) => !open && setDetailAufgabeId(null)}
      >
        <SheetContent
          className="overflow-y-auto"
          sheetKey="kvp-aufgabe-detail"
          defaultWidth={SHEET_WIDTH.xl}
        >
          {detailAufgabeId !== null && <KvpAufgabeDetailContent aufgabeId={detailAufgabeId} />}
        </SheetContent>
      </Sheet>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Ebene-1-Panel: Projekte
// ---------------------------------------------------------------------------

function ProjektPanel({
  projekte,
  ausgewaehlt,
  onAuswahl,
  onNeu,
  onBearbeiten,
}: {
  projekte: KvpProjekt[] | undefined
  ausgewaehlt: number | null
  onAuswahl: (id: number) => void
  onNeu: () => void
  onBearbeiten: (p: KvpProjekt) => void
}) {
  const [suche, setSuche] = useState("")
  const gefiltert = useMemo(() => {
    if (!suche.trim()) return projekte ?? []
    const q = suche.toLowerCase()
    return (projekte ?? []).filter(
      (p) =>
        p.beschreibung.toLowerCase().includes(q) ||
        (p.businessId ?? "").toLowerCase().includes(q),
    )
  }, [projekte, suche])

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-700">
          1. Projekte{projekte ? ` (${projekte.length})` : ""}
        </h2>
        <Button type="button" className="h-8 px-3 text-xs" onClick={onNeu}>
          + Neues Projekt
        </Button>
      </div>
      <Input
        value={suche}
        onChange={(e) => setSuche(e.target.value)}
        placeholder="Projekte filtern …"
        className="mb-2 h-8 text-sm"
        aria-label="Projekte filtern"
      />
      {projekte && projekte.length === 0 ? (
        <div className="py-6 text-center text-sm text-slate-400">
          <p className="mb-2">Noch keine Projekte.</p>
          <Button type="button" variant="secondary" className="h-8 px-3 text-xs" onClick={onNeu}>
            Erstes Projekt anlegen
          </Button>
        </div>
      ) : (
        <ul className="max-h-72 space-y-1 overflow-y-auto pr-1 text-sm" aria-label="Projektliste">
          {gefiltert.map((p) => {
            const aktiv = ausgewaehlt === p.id
            return (
              <li key={p.id} className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => onAuswahl(p.id)}
                  aria-current={aktiv ? "true" : undefined}
                  className={`min-w-0 flex-1 rounded-md border-l-2 px-2 py-1.5 text-left hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 ${
                    aktiv
                      ? "border-slate-900 bg-slate-100 font-medium"
                      : "border-transparent"
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <span className="min-w-0 flex-1 truncate">
                      <span className="font-mono text-xs text-slate-400">{p.businessId}</span>{" "}
                      <span className="text-slate-900">{p.beschreibung}</span>{" "}
                      <span className="text-xs text-slate-400">· {artLabel(p.art)}</span>
                    </span>
                    <AbgeleiteterStatusBadge status={p.abgeleiteterStatus} abbruch={p} />
                  </span>
                  {fortschrittText(p) && (
                    <span className="block text-xs text-slate-400">{fortschrittText(p)}</span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => onBearbeiten(p)}
                  aria-label={`Projekt ${p.businessId ?? p.beschreibung} bearbeiten`}
                  title="Projekt bearbeiten"
                  className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  <Pencil className="size-3.5" />
                </button>
              </li>
            )
          })}
          {gefiltert.length === 0 && projekte && projekte.length > 0 && (
            <li className="py-2 text-center text-slate-400">Kein Treffer.</li>
          )}
        </ul>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Ebene-2-Panel: Teilprojekte (im Kontext des gewählten Projekts)
// ---------------------------------------------------------------------------

function TeilprojektPanel({
  projekt,
  teilprojekte,
  ausgewaehlt,
  onAuswahl,
  onNeu,
  onBearbeiten,
}: {
  projekt: KvpProjekt | null
  teilprojekte: KvpTeilprojekt[] | undefined
  ausgewaehlt: number | null
  onAuswahl: (id: number) => void
  onNeu: () => void
  onBearbeiten: (tb: KvpTeilprojekt) => void
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="min-w-0 truncate text-sm font-semibold text-slate-700">
          2. Teilprojekte{teilprojekte ? ` (${teilprojekte.length})` : ""}
          {projekt && (
            <span className="ml-1 font-normal text-slate-400">
              in <span className="font-mono text-xs">{projekt.businessId}</span>
            </span>
          )}
        </h2>
        <Button
          type="button"
          className="h-8 shrink-0 px-3 text-xs"
          onClick={onNeu}
          disabled={!projekt}
          title={!projekt ? "Zuerst ein Projekt auswählen" : undefined}
        >
          + Neues Teilprojekt
        </Button>
      </div>
      {!projekt ? (
        <p className="py-6 text-center text-sm text-slate-400">
          Links ein Projekt auswählen, um dessen Teilprojekte zu sehen.
        </p>
      ) : teilprojekte && teilprojekte.length === 0 ? (
        <div className="py-6 text-center text-sm text-slate-400">
          <p className="mb-2">Noch keine Teilprojekte — legen Sie das erste an.</p>
          <Button type="button" variant="secondary" className="h-8 px-3 text-xs" onClick={onNeu}>
            Erstes Teilprojekt anlegen
          </Button>
        </div>
      ) : (
        <ul
          className="max-h-72 space-y-1 overflow-y-auto pr-1 text-sm"
          aria-label="Teilprojektliste"
        >
          {teilprojekte?.map((tb) => {
            const aktiv = ausgewaehlt === tb.id
            return (
              <li key={tb.id} className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => onAuswahl(tb.id)}
                  aria-current={aktiv ? "true" : undefined}
                  className={`min-w-0 flex-1 rounded-md border-l-2 px-2 py-1.5 text-left hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 ${
                    aktiv
                      ? "border-slate-900 bg-slate-100 font-medium"
                      : "border-transparent"
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <span className="min-w-0 flex-1 truncate">
                      <span className="font-mono text-xs text-slate-400">{tb.businessId}</span>{" "}
                      <span className="text-slate-900">{tb.bezeichnung}</span>
                    </span>
                    <AbgeleiteterStatusBadge status={tb.abgeleiteterStatus} abbruch={tb} />
                  </span>
                  {fortschrittText(tb) && (
                    <span className="block text-xs text-slate-400">{fortschrittText(tb)}</span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => onBearbeiten(tb)}
                  aria-label={`Teilprojekt ${tb.businessId ?? tb.bezeichnung} bearbeiten`}
                  title="Teilprojekt bearbeiten"
                  className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  <Pencil className="size-3.5" />
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Abbruch-Bereich (Projekt-/Teilprojekt-Sheet): terminaler Abbruch mit
// Pflicht-Grund bzw. Aufheben — Berechtigung serverseitig (kvp_eskalieren).
// ---------------------------------------------------------------------------

function AbbruchBereich({
  ebene,
  objekt,
  pending,
  onAbbrechen,
  onAufheben,
}: {
  ebene: "Projekt" | "Teilprojekt"
  objekt: KvpAbbruchFelder & { abgeleiteterStatus: KvpAbgeleiteterStatus }
  pending: boolean
  onAbbrechen: (grund: string) => void
  onAufheben: () => void
}) {
  const [grund, setGrund] = useState("")
  const istAbgebrochen = objekt.abgebrochenAm !== null

  if (istAbgebrochen) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3">
        <p className="text-sm font-medium text-red-800">
          {ebene} abgebrochen am {formatDatum(objekt.abgebrochenAm)}
          {objekt.abgebrochenVon ? ` von ${objekt.abgebrochenVon}` : ""}
        </p>
        <p className="mt-1 text-sm text-red-700">Grund: {objekt.abbruchGrund ?? "—"}</p>
        {objekt.statusBeiAbbruch && (
          <p className="mt-0.5 text-xs text-red-600">
            Status zum Zeitpunkt des Abbruchs:{" "}
            {ABGELEITET_LABEL[objekt.statusBeiAbbruch as KvpAbgeleiteterStatus] ??
              objekt.statusBeiAbbruch}
          </p>
        )}
        <Button
          type="button"
          variant="secondary"
          className="mt-3"
          disabled={pending}
          onClick={onAufheben}
        >
          Abbruch aufheben
        </Button>
        <p className="mt-1.5 text-xs text-red-600">
          Aufheben entfernt nur den Marker — kaskadiert abgebrochene Aufgaben bleiben
          abgebrochen und werden bei Bedarf einzeln reaktiviert.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-md border border-slate-200 p-3">
      <p className="text-sm font-medium text-slate-700">{ebene} abbrechen</p>
      <p className="mt-1 text-xs text-slate-500">
        Terminal: alle offenen Aufgaben werden mit abgebrochen (Vorher-Status bleibt im
        Verlauf erhalten), erledigte bleiben erledigt. Nur Leitung/Pflege/Admin.
      </p>
      <Textarea
        rows={2}
        className="mt-2"
        value={grund}
        onChange={(e) => setGrund(e.target.value)}
        placeholder="Grund (Pflicht) …"
        aria-label={`Abbruch-Grund für ${ebene}`}
      />
      <Button
        type="button"
        variant="destructive"
        className="mt-2"
        disabled={pending || !grund.trim()}
        onClick={() => onAbbrechen(grund.trim())}
      >
        {ebene} abbrechen
      </Button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sheet: Projekt anlegen/bearbeiten (POST/PUT /kvp/projekte)
// ---------------------------------------------------------------------------

function ProjektSheet({
  zustand,
  onOpenChange,
  onGespeichert,
}: {
  zustand: "neu" | KvpProjekt | null
  onOpenChange: (open: boolean) => void
  onGespeichert: (p: KvpProjekt) => void
}) {
  const istNeu = zustand === "neu"
  const projekt = istNeu || zustand === null ? null : zustand
  const toast = useToast()
  const queryClient = useQueryClient()
  const [beschreibung, setBeschreibung] = useState("")
  const [art, setArt] = useState("projekt")

  const invalidateAlles = () => {
    // Abbruch kaskadiert bis auf Aufgaben-Ebene — alles neu laden
    queryClient.invalidateQueries({ queryKey: ["kvp-projekte"] })
    queryClient.invalidateQueries({ queryKey: ["kvp-teilprojekte"] })
    queryClient.invalidateQueries({ queryKey: ["kvp-aufgaben"] })
    queryClient.invalidateQueries({ queryKey: ["kvp-cockpit"] })
  }

  const abbrechen = useMutation({
    mutationFn: (grund: string) => bricKvpProjektAb(projekt!.id, grund),
    onSuccess: () => {
      toast.success("Projekt abgebrochen.")
      invalidateAlles()
      onOpenChange(false)
    },
    onError: (err) =>
      toast.error(fehlerNachricht(err, "Projekt konnte nicht abgebrochen werden.")),
  })

  const aufheben = useMutation({
    mutationFn: () => hebeKvpProjektAbbruchAuf(projekt!.id),
    onSuccess: () => {
      toast.success("Abbruch aufgehoben.")
      invalidateAlles()
      onOpenChange(false)
    },
    onError: (err) =>
      toast.error(fehlerNachricht(err, "Abbruch konnte nicht aufgehoben werden.")),
  })

  // State-Reset beim Öffnen (Muster UX_KONZEPT.md §7.4)
  useEffect(() => {
    if (zustand === "neu") {
      setBeschreibung("")
      setArt("projekt")
    } else if (zustand) {
      setBeschreibung(zustand.beschreibung)
      setArt(zustand.art)
    }
  }, [zustand])

  const speichern = useMutation({
    mutationFn: () =>
      istNeu
        ? createKvpProjekt({ beschreibung: beschreibung.trim(), art })
        : updateKvpProjekt(projekt!.id, { beschreibung: beschreibung.trim(), art }),
    onSuccess: (p) => {
      toast.success(istNeu ? "Projekt angelegt." : "Projekt gespeichert.")
      onGespeichert(p)
      onOpenChange(false)
    },
    onError: (err) =>
      toast.error(fehlerNachricht(err, "Projekt konnte nicht gespeichert werden.")),
  })

  return (
    <Sheet open={zustand !== null} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="kvp-projekt-form" defaultWidth={SHEET_WIDTH.md}>
        <SheetHeader>
          <SheetTitle>
            {istNeu ? "Neues Projekt" : `Projekt ${projekt?.businessId ?? `#${projekt?.id}`}`}
          </SheetTitle>
          <SheetDescription>
            Oberste KVP-Ebene (05-A). Die Business-ID (BR_…) wird automatisch vergeben.
          </SheetDescription>
          {projekt && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-600">Status:</span>
              <AbgeleiteterStatusBadge status={projekt.abgeleiteterStatus} abbruch={projekt} />
              <span
                className="text-xs text-slate-400"
                title="Der Status ergibt sich automatisch aus den Teilprojekten bzw. deren Aufgaben."
              >
                (abgeleitet aus den Teilprojekten)
              </span>
            </div>
          )}
        </SheetHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (beschreibung.trim()) speichern.mutate()
          }}
        >
          <div className="space-y-1.5">
            <label htmlFor="kvp-projekt-beschreibung" className="block text-sm text-slate-700">
              Beschreibung <span className="text-red-600">*</span>
            </label>
            <Textarea
              id="kvp-projekt-beschreibung"
              required
              rows={3}
              value={beschreibung}
              onChange={(e) => setBeschreibung(e.target.value)}
              placeholder="z. B. Arbeitssicherheit"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="kvp-projekt-art" className="block text-sm text-slate-700">
              Art
            </label>
            <Select id="kvp-projekt-art" value={art} onChange={(e) => setArt(e.target.value)}>
              {PROJEKT_ARTEN.map((a) => (
                <option key={a.wert} value={a.wert}>
                  {a.label}
                </option>
              ))}
            </Select>
          </div>
          {projekt && (
            <AbbruchBereich
              ebene="Projekt"
              objekt={projekt}
              pending={abbrechen.isPending || aufheben.isPending}
              onAbbrechen={(grund) => abbrechen.mutate(grund)}
              onAufheben={() => aufheben.mutate()}
            />
          )}
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={speichern.isPending}>
              {istNeu ? "Projekt anlegen" : "Speichern"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

// ---------------------------------------------------------------------------
// Sheet: Teilprojekt anlegen/bearbeiten (POST/PUT /kvp/teilprojekte)
// ---------------------------------------------------------------------------

interface TeilprojektForm {
  bezeichnung: string
  beschreibung: string
  projektleiterEmployeeId: string | null
  start: string
  geplantesEnde: string
  tatsaechlichesEnde: string
}

const TEILPROJEKT_LEER: TeilprojektForm = {
  bezeichnung: "",
  beschreibung: "",
  projektleiterEmployeeId: null,
  start: "",
  geplantesEnde: "",
  tatsaechlichesEnde: "",
}

function TeilprojektSheet({
  zustand,
  projekt,
  onOpenChange,
  onGespeichert,
}: {
  zustand: "neu" | KvpTeilprojekt | null
  projekt: KvpProjekt
  onOpenChange: (open: boolean) => void
  onGespeichert: (tb: KvpTeilprojekt) => void
}) {
  const istNeu = zustand === "neu"
  const teilprojekt = istNeu || zustand === null ? null : zustand
  const toast = useToast()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<TeilprojektForm>(TEILPROJEKT_LEER)

  useEffect(() => {
    if (zustand === "neu") setForm(TEILPROJEKT_LEER)
    else if (zustand)
      setForm({
        bezeichnung: zustand.bezeichnung,
        beschreibung: zustand.beschreibung ?? "",
        projektleiterEmployeeId: zustand.projektleiterEmployeeId,
        start: alsDateInput(zustand.start),
        geplantesEnde: alsDateInput(zustand.geplantesEnde),
        tatsaechlichesEnde: alsDateInput(zustand.tatsaechlichesEnde),
      })
  }, [zustand])

  const speichern = useMutation({
    mutationFn: () =>
      istNeu
        ? createKvpTeilprojekt({
            projektId: projekt.id,
            bezeichnung: form.bezeichnung.trim(),
            beschreibung: form.beschreibung.trim() || undefined,
            projektleiterEmployeeId: form.projektleiterEmployeeId ?? undefined,
          })
        : updateKvpTeilprojekt(teilprojekt!.id, {
            bezeichnung: form.bezeichnung.trim(),
            beschreibung: form.beschreibung.trim() || null,
            projektleiterEmployeeId: form.projektleiterEmployeeId,
            start: form.start || null,
            geplantesEnde: form.geplantesEnde || null,
            tatsaechlichesEnde: form.tatsaechlichesEnde || null,
          }),
    onSuccess: (tb) => {
      toast.success(istNeu ? "Teilprojekt angelegt." : "Teilprojekt gespeichert.")
      onGespeichert(tb)
      onOpenChange(false)
    },
    onError: (err) =>
      toast.error(fehlerNachricht(err, "Teilprojekt konnte nicht gespeichert werden.")),
  })

  const invalidateAlles = () => {
    // Abbruch kaskadiert auf die offenen Aufgaben — alles neu laden
    queryClient.invalidateQueries({ queryKey: ["kvp-projekte"] })
    queryClient.invalidateQueries({ queryKey: ["kvp-teilprojekte"] })
    queryClient.invalidateQueries({ queryKey: ["kvp-aufgaben"] })
    queryClient.invalidateQueries({ queryKey: ["kvp-cockpit"] })
  }

  const abbrechen = useMutation({
    mutationFn: (grund: string) => bricKvpTeilprojektAb(teilprojekt!.id, grund),
    onSuccess: () => {
      toast.success("Teilprojekt abgebrochen — offene Aufgaben wurden mit abgebrochen.")
      invalidateAlles()
      onOpenChange(false)
    },
    onError: (err) =>
      toast.error(fehlerNachricht(err, "Teilprojekt konnte nicht abgebrochen werden.")),
  })

  const aufheben = useMutation({
    mutationFn: () => hebeKvpTeilprojektAbbruchAuf(teilprojekt!.id),
    onSuccess: () => {
      toast.success("Abbruch aufgehoben.")
      invalidateAlles()
      onOpenChange(false)
    },
    onError: (err) =>
      toast.error(fehlerNachricht(err, "Abbruch konnte nicht aufgehoben werden.")),
  })

  return (
    <Sheet open={zustand !== null} onOpenChange={onOpenChange}>
      <SheetContent
        className="overflow-y-auto"
        sheetKey="kvp-teilprojekt-form"
        defaultWidth={SHEET_WIDTH.lg}
      >
        <SheetHeader>
          <SheetTitle>
            {istNeu
              ? "Neues Teilprojekt"
              : `Teilprojekt ${teilprojekt?.businessId ?? `#${teilprojekt?.id}`}`}
          </SheetTitle>
          <SheetDescription>
            Gehört zu Projekt{" "}
            <span className="font-mono text-xs">{projekt.businessId}</span>{" "}
            {`„${projekt.beschreibung}“`}. Die Business-ID (…_TB_…) wird automatisch vergeben.
          </SheetDescription>
          {teilprojekt && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-600">Status:</span>
              <AbgeleiteterStatusBadge
                status={teilprojekt.abgeleiteterStatus}
                abbruch={teilprojekt}
              />
              <span
                className="text-xs text-slate-400"
                title="Der Status ergibt sich automatisch aus den Aufgaben (neu / in Arbeit / abgeschlossen / verworfen)."
              >
                (abgeleitet aus den Aufgaben{fortschrittText(teilprojekt) ? ` — ${fortschrittText(teilprojekt)}` : ""})
              </span>
            </div>
          )}
        </SheetHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (form.bezeichnung.trim()) speichern.mutate()
          }}
        >
          <div className="space-y-1.5">
            <label htmlFor="kvp-tb-bezeichnung" className="block text-sm text-slate-700">
              Bezeichnung <span className="text-red-600">*</span>
            </label>
            <Input
              id="kvp-tb-bezeichnung"
              required
              value={form.bezeichnung}
              onChange={(e) => setForm({ ...form, bezeichnung: e.target.value })}
              placeholder="z. B. Unterweisungen 2026"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="kvp-tb-beschreibung" className="block text-sm text-slate-700">
              Beschreibung
            </label>
            <Textarea
              id="kvp-tb-beschreibung"
              rows={3}
              value={form.beschreibung}
              onChange={(e) => setForm({ ...form, beschreibung: e.target.value })}
            />
          </div>
          <EmployeePicker
            label="Projektleiter"
            value={form.projektleiterEmployeeId}
            onChange={(id) => setForm({ ...form, projektleiterEmployeeId: id })}
          />
          {!istNeu && (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <label htmlFor="kvp-tb-start" className="block text-sm text-slate-700">
                    Start
                  </label>
                  <Input
                    id="kvp-tb-start"
                    type="date"
                    value={form.start}
                    onChange={(e) => setForm({ ...form, start: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="kvp-tb-geplant" className="block text-sm text-slate-700">
                    Geplantes Ende
                  </label>
                  <Input
                    id="kvp-tb-geplant"
                    type="date"
                    value={form.geplantesEnde}
                    onChange={(e) => setForm({ ...form, geplantesEnde: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="kvp-tb-ende" className="block text-sm text-slate-700">
                    Tatsächliches Ende
                  </label>
                  <Input
                    id="kvp-tb-ende"
                    type="date"
                    value={form.tatsaechlichesEnde}
                    onChange={(e) => setForm({ ...form, tatsaechlichesEnde: e.target.value })}
                  />
                </div>
              </div>
            </>
          )}
          {teilprojekt && (
            <AbbruchBereich
              ebene="Teilprojekt"
              objekt={teilprojekt}
              pending={abbrechen.isPending || aufheben.isPending}
              onAbbrechen={(grund) => abbrechen.mutate(grund)}
              onAufheben={() => aufheben.mutate()}
            />
          )}
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={speichern.isPending}>
              {istNeu ? "Teilprojekt anlegen" : "Speichern"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

// ---------------------------------------------------------------------------
// Sheet: Aufgabe anlegen (POST /kvp/aufgaben) — Bearbeiten läuft im Detail-Sheet
// ---------------------------------------------------------------------------

interface AufgabeForm {
  bezeichnung: string
  statusId: string
  prioritaet: string
  zustaendigEmployeeId: string | null
  geplantesEnde: string
  zeitbedarfStunden: string
}

const AUFGABE_LEER: AufgabeForm = {
  bezeichnung: "",
  statusId: "",
  prioritaet: "mittel",
  zustaendigEmployeeId: null,
  geplantesEnde: "",
  zeitbedarfStunden: "",
}

function AufgabeNeuSheet({
  open,
  projekt,
  teilprojekt,
  statusListe,
  onOpenChange,
  onGespeichert,
}: {
  open: boolean
  projekt: KvpProjekt
  teilprojekt: KvpTeilprojekt
  statusListe: KvpStatus[] | undefined
  onOpenChange: (open: boolean) => void
  onGespeichert: () => void
}) {
  const toast = useToast()
  const [form, setForm] = useState<AufgabeForm>(AUFGABE_LEER)

  useEffect(() => {
    if (open) setForm(AUFGABE_LEER)
  }, [open])

  const aktiveStatus = useMemo(
    () => (statusListe ?? []).filter((s) => s.aktiv),
    [statusListe],
  )

  const speichern = useMutation({
    mutationFn: () =>
      createKvpAufgabe({
        teilprojektId: teilprojekt.id,
        bezeichnung: form.bezeichnung.trim(),
        statusId: form.statusId ? Number(form.statusId) : undefined,
        prioritaet: form.prioritaet || undefined,
        zustaendigEmployeeId: form.zustaendigEmployeeId ?? undefined,
        geplantesEnde: form.geplantesEnde || undefined,
        zeitbedarfStunden: form.zeitbedarfStunden ? Number(form.zeitbedarfStunden) : undefined,
      }),
    onSuccess: () => {
      toast.success("Aufgabe angelegt.")
      onGespeichert()
      onOpenChange(false)
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Aufgabe konnte nicht angelegt werden.")),
  })

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="overflow-y-auto"
        sheetKey="kvp-aufgabe-form"
        defaultWidth={SHEET_WIDTH.lg}
      >
        <SheetHeader>
          <SheetTitle>Neue Aufgabe</SheetTitle>
          <SheetDescription>
            Wird angelegt in{" "}
            <span className="font-mono text-xs">{projekt.businessId}</span> ›{" "}
            <span className="font-mono text-xs">{teilprojekt.businessId}</span>{" "}
            {`„${teilprojekt.bezeichnung}“`}. Aus geplantem Ende + Zeitbedarf berechnet der Server
            den spätesten Anfangstermin (Rückwärtsterminierung).
          </SheetDescription>
        </SheetHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (form.bezeichnung.trim()) speichern.mutate()
          }}
        >
          <div className="space-y-1.5">
            <label htmlFor="kvp-af-bezeichnung" className="block text-sm text-slate-700">
              Bezeichnung <span className="text-red-600">*</span>
            </label>
            <Textarea
              id="kvp-af-bezeichnung"
              required
              rows={3}
              value={form.bezeichnung}
              onChange={(e) => setForm({ ...form, bezeichnung: e.target.value })}
              placeholder="Was ist zu tun?"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="kvp-af-status" className="block text-sm text-slate-700">
                Status
              </label>
              <Select
                id="kvp-af-status"
                value={form.statusId}
                onChange={(e) => setForm({ ...form, statusId: e.target.value })}
              >
                <option value="">—</option>
                {aktiveStatus.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="kvp-af-prio" className="block text-sm text-slate-700">
                Priorität
              </label>
              <Select
                id="kvp-af-prio"
                value={form.prioritaet}
                onChange={(e) => setForm({ ...form, prioritaet: e.target.value })}
              >
                <option value="">—</option>
                {PRIORITAETEN.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <EmployeePicker
            label="Zuständig"
            value={form.zustaendigEmployeeId}
            onChange={(id) => setForm({ ...form, zustaendigEmployeeId: id })}
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="kvp-af-ende" className="block text-sm text-slate-700">
                Geplantes Ende
              </label>
              <Input
                id="kvp-af-ende"
                type="date"
                value={form.geplantesEnde}
                onChange={(e) => setForm({ ...form, geplantesEnde: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="kvp-af-zeitbedarf" className="block text-sm text-slate-700">
                Zeitbedarf (Stunden)
              </label>
              <Input
                id="kvp-af-zeitbedarf"
                type="number"
                min="0"
                step="0.25"
                value={form.zeitbedarfStunden}
                onChange={(e) => setForm({ ...form, zeitbedarfStunden: e.target.value })}
              />
            </div>
          </div>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={speichern.isPending}>
              Aufgabe anlegen
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
